import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Body,
  Title,
  Flex,
  Input,
  Select,
  Button,
  Tooltip,
  Icon,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import {
  Access,
  useCreateAccessMutation,
  useUpdateAccessMutation,
  Role,
} from 'types/dt-graphql-apollo-v2';
import { v1 as uuidv1 } from 'uuid';
import { validateEmail } from 'utils/misc';
import mixpanel from 'mixpanel-browser';
import { useUrl } from 'hooks/useUrls';
import { notification, Collapse } from 'antd';
import pluralize from 'pluralize';
import { AccessControlMatrix } from './AccessControlMatrix';

type EmailItems = Record<string, string>;

type UserRoleSelect = {
  label: Role;
  value: Role;
};

const accessRoles: UserRoleSelect[] = [
  { label: Role.Editor, value: Role.Editor },
  { label: Role.Admin, value: Role.Admin },
];
const defaultUserRole = accessRoles[0];

type AddEditMemberDrawerProps = {
  visible?: boolean;
  selectedUserAccess?: Access | null;
  onOk: () => void;
  onCancel: () => void;
};

export const AddEditMemberDrawer = ({
  visible,
  selectedUserAccess,
  onOk,
  onCancel,
}: AddEditMemberDrawerProps) => {
  const tenant = useUrl.Tenant();

  const [loading, setLoading] = useState(false);
  const [showRoleInformation, setShowRoleInformation] = useState(false);
  const [userRole, setUserRole] = useState(defaultUserRole);
  const [userEmails, setUserEmails] = useState<EmailItems>({ [uuidv1()]: '' });

  const [createAccess] = useCreateAccessMutation();
  const [updateAccess] = useUpdateAccessMutation();

  useEffect(() => {
    if (selectedUserAccess) {
      const role = accessRoles.find(
        (access) => access.value === selectedUserAccess.role
      );
      setUserRole(role || defaultUserRole);
      setUserEmails({
        [selectedUserAccess.externalId]: selectedUserAccess.userId,
      });
    } else {
      clearDrawer();
    }
  }, [selectedUserAccess]);

  const invalidUserRole = !userRole.value.trim();
  const invalidUserEmails =
    Object.values(userEmails).some(
      (email: string) => !email.trim() || !validateEmail(email)
    ) || Object.keys(userEmails).length === 0;
  const hasInvalidValues = invalidUserRole || invalidUserEmails;

  const clearDrawer = () => {
    setUserRole(defaultUserRole);
    setUserEmails({ [uuidv1()]: '' });
  };

  const onAddEmptyEmailField = () => {
    const id = uuidv1();
    setUserEmails((currentState) => ({
      ...currentState,
      [id]: '',
    }));
  };

  const onHandleRemoveEmail = (emailToDelete: string) => {
    setUserEmails((currentState) => {
      Object.keys(currentState).forEach((key) => {
        if (key === emailToDelete) delete currentState[key];
      });
      return { ...currentState };
    });
  };

  const onHandleEditEmail = (
    e: React.FormEvent<HTMLInputElement>,
    emailItemId: string
  ) => {
    const emailInput = e.currentTarget.value.toLowerCase();

    setUserEmails((currentState) => ({
      ...currentState,
      [emailItemId]: emailInput,
    }));
  };

  /** Create one or multiple user Access in the current tenant. */
  const onAddMembersToTenant = async () => {
    const usersCount = Object.keys(userEmails).length;
    if (usersCount === 0) return;

    setLoading(true);
    try {
      const requests = Object.keys(userEmails).map(async (id) => {
        await createAccess({
          variables: {
            id: uuidv1(),
            userId: userEmails[id],
            role: userRole.value,
            tenant,
          },
        });
      });
      await Promise.allSettled(requests);

      mixpanel.track(
        `Configured Access Control and added ${pluralize(
          'user',
          usersCount,
          true
        )}.`,
        {
          tenant,
          role: userRole.value,
        }
      );
      notification.success({
        message: `Users were added`,
        description: `You have successfully added ${usersCount} more ${pluralize(
          'user',
          usersCount
        )} with ${userRole.value} access.`,
      });
      clearDrawer();
    } catch (err: any) {
      notification.error({
        message: `Could not add users.`,
        description: err.toString(),
      });
    } finally {
      setLoading(false);
      onOk();
    }
  };

  /** Update one user Access in the current tenant. */
  const onUpdateMember = async () => {
    const usersCount = Object.keys(userEmails).length;
    if (usersCount === 0) return;

    setLoading(true);
    try {
      const id = Object.keys(userEmails)[0];
      const email = Object.values(userEmails)[0];
      const role = userRole.value;

      await updateAccess({
        variables: {
          id,
          userId: email,
          role,
        },
      });

      mixpanel.track(`Configured Access Control and updated one user.`, {
        tenant,
        role: userRole.value,
      });
      notification.success({
        message: `User was edited.`,
        description: `You have successfully saved ${email} as ${role} in your project.`,
      });
      clearDrawer();
    } catch (err: any) {
      notification.error({
        message: `Could not edit user.`,
        description: err.toString(),
      });
    } finally {
      setLoading(false);
      onOk();
    }
  };

  const handleErrorMessages = () => {
    if (invalidUserRole) {
      return 'The user role is not specified.';
    }
    if (invalidUserEmails) {
      return selectedUserAccess
        ? 'The provided email is empty or invalid.'
        : 'One or more emails are empty or invalid.';
    }
    return 'Unable to add users due to invalid configurations.';
  };

  const renderFooter = () => (
    <Flex gap={10} justifyContent="flex-end">
      <Button disabled={loading} onClick={onCancel}>
        Cancel
      </Button>
      <Tooltip
        content={handleErrorMessages()}
        disabled={!loading && !hasInvalidValues}
      >
        <Button
          type="primary"
          onClick={() =>
            selectedUserAccess ? onUpdateMember() : onAddMembersToTenant()
          }
          loading={loading}
          disabled={loading || hasInvalidValues}
        >
          {selectedUserAccess
            ? `Save and update`
            : `Add ${pluralize('user', Object.keys(userEmails).length)}`}
        </Button>
      </Tooltip>
    </Flex>
  );

  return (
    <StyledDrawer
      width="50vw"
      visible={visible}
      footer={renderFooter()}
      onCancel={onCancel}
    >
      <StyledHeader direction="column" gap={10} alignItems="flex-start">
        <Title level={3}>
          {selectedUserAccess ? 'Edit user access' : 'Add user'}
        </Title>
        {!selectedUserAccess && (
          <Body level={2} className="cogs-body-2">
            Add one or more privileged user with the same role.
          </Body>
        )}
      </StyledHeader>

      <StyledFlex direction="column" gap={20} alignItems="flex-start">
        <Flex direction="column" gap={10} alignItems="flex-start">
          <Title level={5}>User role</Title>
          <Body level={2}>
            Select one of the predefined user roles that suit the user(s) that
            you want to add.
          </Body>
        </Flex>

        <Flex direction="column" gap={10} alignItems="flex-start">
          <Body level={2}>User role</Body>
          <StyledSelectorWrapper>
            <Select
              value={userRole || defaultUserRole}
              options={accessRoles}
              onChange={setUserRole}
            />
          </StyledSelectorWrapper>

          <StyledCollapse
            active={showRoleInformation}
            bordered={false}
            expandIconPosition="right"
            expandIcon={({ isActive }) => (
              <Icon type={isActive ? 'ChevronUpSmall' : 'ChevronDownSmall'} />
            )}
            ghost
            onChange={() => setShowRoleInformation(!showRoleInformation)}
          >
            <Collapse.Panel
              header="Read about the role access"
              key="Read about the role access"
            >
              <AccessControlMatrix role={userRole.value} />
            </Collapse.Panel>
          </StyledCollapse>
        </Flex>
      </StyledFlex>

      <Divider />

      <StyledFlex direction="column" gap={20} alignItems="flex-start">
        <Flex direction="column" gap={10} alignItems="flex-start">
          {selectedUserAccess ? (
            <Title level={5}>User</Title>
          ) : (
            <Title level={5}>Add users</Title>
          )}

          <Body level={2}>
            The user also needs to be added to the identity provider service of
            your company.
          </Body>
        </Flex>

        <Flex direction="column" gap={10} alignItems="flex-start">
          <Body level={2}>Email address</Body>
          {Object.keys(userEmails).map((itemId) => (
            <Flex gap={7} key={itemId}>
              <StyledSelectorWrapper>
                <Input
                  fullWidth
                  value={userEmails[itemId]}
                  placeholder="Type email address"
                  type="email"
                  onChange={(value) => onHandleEditEmail(value, itemId)}
                />
              </StyledSelectorWrapper>
              {!selectedUserAccess && (
                <Button
                  icon="Delete"
                  aria-label="Delete email"
                  onClick={() => onHandleRemoveEmail(itemId)}
                />
              )}
            </Flex>
          ))}
          {!selectedUserAccess && (
            <Button
              icon="Add"
              aria-label="Add email"
              onClick={() => onAddEmptyEmailField()}
            />
          )}
        </Flex>
      </StyledFlex>
    </StyledDrawer>
  );
};

const StyledDrawer = styled(Drawer)`
  overflow: hidden;
  .cogs-drawer-header {
    width: 16px;
    height: 32px;
    position: absolute;
    right: 16px;
    top: 16px;
    display: flex;
    align-items: center;
  }
  .cogs-drawer-close {
    cursor: pointer;
  }
  .cogs-drawer-content {
    padding: 0;
  }
`;

const StyledFlex = styled(Flex)`
  padding: 2rem;
`;

const StyledHeader = styled(StyledFlex)`
  border-bottom: 1px solid var(--cogs-border-default);
`;

const StyledSelectorWrapper = styled.div`
  &&,
  .cogs-btn {
    width: 25vw;
    max-width: 250px;
  }

  .cogs-select {
    .cogs-select__control,
    .cogs-select__control .cogs-select__single-value,
    .cogs-select__menu .cogs-select__option {
      cursor: pointer;
    }
  }
`;

const StyledCollapse = styled(Collapse)<{ active: boolean }>`
  .ant-collapse-header {
    border-radius: 10px !important;
    width: 25vw;
    max-width: 250px;

    ${(props) =>
      props.active &&
      `
      background: var(--cogs-midblue-7);
      color: var(--cogs-midblue-1) !important;
    `}
  }
`;

const Divider = styled.div`
  margin: 0 1rem;
  border-bottom: 1px solid var(--cogs-border-default);
`;
